import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Plus, Filter, Settings2, X } from 'lucide-react';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { ProjectCreationModal } from '@/components/project-creation-modal';
import { ColumnConfiguratorDrawer, DEFAULT_COLUMNS, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { ProjectFilterSheet, applyColumnFilters, countActiveFilters, getProjectColumnValue, type ColumnFilters } from '@/components/project-filter-sheet';
import { useProjectForm } from '@/providers/ProjectFormProvider';
import { ProjectsGrid } from '@/components/projects-grid';
import type { ColumnSort } from '@/components/ui/data-table-enhanced';
import { fetchProjects, fetchClients, fetchEmployees, fetchTasks, fetchMilestones, fetchResourceAllocations, type ProjectFilterParams } from '@/lib/data-service';
import { useErrorHandler } from '@/hooks/use-error-handler';
import type { Project, Client, Employee, ProjectHealth, Task, Milestone, ResourceAllocation } from '@/types';
import type { PaginationMetadata } from '@/types/pagination';

import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import { usePagination } from '@/hooks/use-pagination';
import { useKeyboardPagination } from '@/hooks/use-keyboard-pagination';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import { usePersistedFilters } from '@/hooks/use-persisted-filters';
import { ROUTES } from '@/routes';
import { useCompany } from '@/contexts/CompanyContext';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import { PROJECT_STATUS_LABELS } from '@/types';

interface ProjectsPageProps {
  onNavigateToProject?: (projectId: number) => void;
}

export function ProjectsPage({ onNavigateToProject }: ProjectsPageProps = {}) {
  const navigate = useNavigate();
  const { currentCompany } = useCompany();
  const { selectableStatuses } = useProjectStatuses();
  const { hasMinimizedForm, formState, acknowledgeModalOpen, restoreForm } = useProjectForm();
  const { preferences } = useUserPreferences();
  const tableRef = useRef<HTMLDivElement>(null);
  const { handleApiError } = useErrorHandler({ useToast: true });
  
  // Pagination (don't persist to URL to keep URLs clean)
  const {
    currentPage,
    pageSize,
    setPage,
    setPageSize,
    resetPagination,
  } = usePagination({ defaultPageSize: 25, persistInUrl: false });
  
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata>({
    currentPage: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    startIndex: 0,
    endIndex: 0,
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [resourceAllocations, setResourceAllocations] = useState<ResourceAllocation[]>([]);
  
  // Persisted filters - will be restored when navigating back to this page
  const { filters, updateFilter, resetFilters } = usePersistedFilters({
    storageKey: 'projects-list-filters',
    defaultFilters: {
      searchTerm: '',
      statusFilter: 'all' as string,
      healthFilter: 'all' as ProjectHealth | 'all',
      sortBy: 'health' as 'name' | 'health' | 'value' | 'completion',
    },
  });
  
  const { searchTerm, statusFilter, healthFilter, sortBy } = filters;
  
  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || healthFilter !== 'all';
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});

  // Multi-column sort state: ordered array where first entry = primary sort, second = secondary, etc.
  // Click cycles: none -> asc -> desc -> remove
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([]);

  // Full dataset cache for client-side column sorting (null = use server pagination)
  const [allProjectsForSort, setAllProjectsForSort] = useState<Project[] | null>(null);
  
  // Use column preferences hook for persistence (API in live mode, localStorage in demo mode)
  const { columns, updateColumns } = useColumnPreferences({
    storageKey: 'projects-column-preferences',
    defaultColumns: DEFAULT_COLUMNS,
  });

  // Load reference data when component mounts or company changes
  useEffect(() => {
    async function loadReferenceData() {
      try {
        const [clientsData, employeesData, tasksData, milestonesData, allocationsData] = await Promise.all([
          fetchClients('projects'),
          fetchEmployees('projects'),
          fetchTasks('projects'),
          fetchMilestones('projects'),
          fetchResourceAllocations('projects'),
        ]);
        setClients(clientsData);
        setEmployees(employeesData);
        setTasks(tasksData);
        setMilestones(milestonesData);
        setResourceAllocations(allocationsData);
      } catch (error) {
        handleApiError(error, '/api/reference-data', 'GET');
      }
    }
    loadReferenceData();
  }, [handleApiError, currentCompany?.id]);

  // Open modal when form should be restored
  useEffect(() => {
    if (formState?.shouldOpenModal) {
      setCreateModalOpen(true);
      acknowledgeModalOpen();
    }
  }, [formState?.shouldOpenModal, acknowledgeModalOpen]);

  // Debounced search term for API calls (to avoid too many requests while typing)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Function to load/refresh projects with pagination and filters
  const loadProjects = async (
    page: number = currentPage, 
    size: number = pageSize,
    filters?: ProjectFilterParams
  ) => {
    // Show loading state for initial load, transition state for page changes
    if (projects.length === 0) {
      setLoading(true);
    } else {
      setIsTransitioning(true);
    }

    try {
      // Build filter params
      const filterParams: ProjectFilterParams = filters || {
        statusId: statusFilter !== 'all' ? Number(statusFilter) : undefined,
        health: healthFilter,
        search: debouncedSearchTerm,
        sortBy: sortBy,
      };

      // Fetch projects with pagination and filter params
      const result = await fetchProjects('projects', { page, pageSize: size }, filterParams);
      
      // Check if result is a paginated response
      if (result && typeof result === 'object' && 'data' in result && 'pagination' in result) {
        // Server returned paginated response
        setProjects(result.data);
        setPagination({
          currentPage: result.pagination.currentPage,
          pageSize: result.pagination.pageSize,
          totalItems: result.pagination.totalItems,
          totalPages: result.pagination.totalPages,
          hasNextPage: result.pagination.hasNextPage,
          hasPreviousPage: result.pagination.hasPreviousPage,
          startIndex: result.pagination.startIndex,
          endIndex: result.pagination.endIndex,
        });
      } else {
        // Fallback: result is an array (demo mode or non-paginated response)
        const projectsArray = Array.isArray(result) ? result : [];
        setProjects(projectsArray);
        
        // Calculate pagination client-side for demo mode
        const totalItems = projectsArray.length;
        const totalPages = Math.ceil(totalItems / size);
        const safePage = Math.max(1, Math.min(page, totalPages || 1));
        const startIndex = (safePage - 1) * size;
        const endIndex = Math.min(startIndex + size, totalItems);
        
        setPagination({
          currentPage: safePage,
          pageSize: size,
          totalItems,
          totalPages,
          hasNextPage: safePage < totalPages,
          hasPreviousPage: safePage > 1,
          startIndex,
          endIndex,
        });
      }
      setLoading(false);
    } catch (error) {
      handleApiError(error, '/api/projects', 'GET');
      setLoading(false);
    } finally {
      setIsTransitioning(false);
    }
  };

  // Load projects when page, pageSize, or filters change
  useEffect(() => {
    // Skip server-side pagination when column sorts are active
    // (we fetch all data separately and handle sorting + pagination client-side)
    if (columnSorts.length > 0) return;

    loadProjects(currentPage, pageSize, {
      statusId: statusFilter !== 'all' ? Number(statusFilter) : undefined,
      health: healthFilter,
      search: debouncedSearchTerm,
      sortBy: sortBy,
    });
  }, [currentPage, pageSize, statusFilter, healthFilter, debouncedSearchTerm, sortBy, currentCompany?.id, columnSorts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // When column sorts are active, fetch ALL projects so sorting works across the full dataset
  useEffect(() => {
    if (columnSorts.length === 0) {
      setAllProjectsForSort(null);
      return;
    }

    let cancelled = false;
    const fetchAllForSort = async () => {
      setIsTransitioning(true);
      try {
        const filterParams: ProjectFilterParams = {
          statusId: statusFilter !== 'all' ? Number(statusFilter) : undefined,
          health: healthFilter,
          search: debouncedSearchTerm,
          sortBy,
        };
        const result = await fetchProjects('projects', { page: 1, pageSize: 10000 }, filterParams);
        if (cancelled) return;

        let all: Project[];
        if (result && typeof result === 'object' && 'data' in result && 'pagination' in result) {
          all = Array.isArray(result.data) ? result.data : [];
        } else {
          all = Array.isArray(result) ? result : [];
        }
        setAllProjectsForSort(all);
      } catch (error) {
        if (!cancelled) console.error('Failed to fetch all projects for sorting:', error);
      } finally {
        if (!cancelled) setIsTransitioning(false);
      }
    };
    fetchAllForSort();

    return () => { cancelled = true; };
  }, [columnSorts.length > 0 ? 1 : 0, statusFilter, healthFilter, debouncedSearchTerm, sortBy, currentCompany?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalculate client-side pagination when sorting from full dataset
  useEffect(() => {
    if (!allProjectsForSort || columnSorts.length === 0) return;

    const filtered = applyColumnFilters(allProjectsForSort, columnFilters);
    const totalItems = filtered.length;
    const totalPages = Math.ceil(totalItems / pageSize) || 1;
    const safePage = Math.max(1, Math.min(currentPage, totalPages));

    setPagination({
      currentPage: safePage,
      pageSize,
      totalItems,
      totalPages,
      hasNextPage: safePage < totalPages,
      hasPreviousPage: safePage > 1,
      startIndex: (safePage - 1) * pageSize,
      endIndex: Math.min(safePage * pageSize, totalItems),
    });
  }, [allProjectsForSort, columnFilters, currentPage, pageSize, columnSorts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to page 1 when filters or column sorts change (but not when page/pageSize changes)
  const prevFiltersRef = useRef({ statusFilter, healthFilter, debouncedSearchTerm, sortBy, columnSortsLen: columnSorts.length });
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const filtersChanged = 
      prevFilters.statusFilter !== statusFilter ||
      prevFilters.healthFilter !== healthFilter ||
      prevFilters.debouncedSearchTerm !== debouncedSearchTerm ||
      prevFilters.sortBy !== sortBy ||
      prevFilters.columnSortsLen !== columnSorts.length;
    
    if (filtersChanged && currentPage !== 1) {
      resetPagination();
    }
    
    prevFiltersRef.current = { statusFilter, healthFilter, debouncedSearchTerm, sortBy, columnSortsLen: columnSorts.length };
  }, [statusFilter, healthFilter, debouncedSearchTerm, sortBy, columnSorts.length, currentPage, resetPagination]);

  // Track the previous page to detect actual page changes
  const prevPageRef = useRef(currentPage);

  // Scroll to top when page changes (but not on initial load or filter changes)
  useEffect(() => {
    // Only scroll if the page number actually changed (not on initial mount or filter changes)
    if (prevPageRef.current !== currentPage && prevPageRef.current !== undefined) {
      if (tableRef.current) {
        tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    prevPageRef.current = currentPage;
  }, [currentPage]);

  // Keyboard shortcuts for pagination
  useKeyboardPagination({
    currentPage,
    totalPages: pagination.totalPages,
    onPageChange: setPage,
    enabled: !loading && !createModalOpen && !columnConfigOpen,
  });

  // Get current page of projects
  // When column sorts are active: uses allProjectsForSort (full dataset) -> filter -> sort -> slice for page
  // When no column sorts: uses projects (server-paged data) -> filter only
  const paginatedProjects = useMemo(() => {
    const source = allProjectsForSort ?? projects;
    const filtered = applyColumnFilters(source, columnFilters);

    if (columnSorts.length === 0) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      for (const { columnId, direction } of columnSorts) {
        const aVal = getProjectColumnValue(columnId, a);
        const bVal = getProjectColumnValue(columnId, b);

        // Handle undefined values - push them to the end
        if (aVal === undefined && bVal === undefined) continue;
        if (aVal === undefined) return 1;
        if (bVal === undefined) return -1;

        let cmp = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          cmp = aVal - bVal;
        } else {
          cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
        }

        if (cmp !== 0) {
          return direction === 'asc' ? cmp : -cmp;
        }
        // If equal, fall through to next sort column
      }
      return 0;
    });

    // Client-side pagination: slice the sorted full dataset for the current page
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [allProjectsForSort, projects, columnFilters, columnSorts, currentPage, pageSize]);

  // Count active column filters for the badge on the Filter button
  const activeColumnFilterCount = useMemo(() => countActiveFilters(columnFilters), [columnFilters]);

  // Get visible columns sorted by order (needed for ProjectFilterSheet)
  const visibleColumns = columns
    .filter(col => col.visible)
    .sort((a, b) => a.order - b.order);

  const handleSaveColumns = (newColumns: ColumnDefinition[]) => {
    updateColumns(newColumns);
  };

  const handleNewProjectClick = () => {
    if (hasMinimizedForm) {
      restoreForm();
    }
    setCreateModalOpen(true);
  };

  // Reference data bundle for the grid component
  const referenceData = useMemo(() => ({
    clients,
    employees,
    tasks,
    milestones,
    resourceAllocations,
  }), [clients, employees, tasks, milestones, resourceAllocations]);

  // Derive available options for filter sheet MultiSelect fields
  const filterAvailableOptions = useMemo(() => {
    const allProjects = allProjectsForSort ?? projects;
    const opts: Record<string, { value: string; label: string }[]> = {};

    // Project Name
    const projectNames = [...new Set(allProjects.map(p => p.name).filter(Boolean))];
    if (projectNames.length > 0) {
      opts['project-name'] = projectNames.sort().map(n => ({ value: n, label: n }));
    }

    // Project Code
    const projectCodes = [...new Set(allProjects.map(p => p.code).filter((c): c is string => !!c))];
    if (projectCodes.length > 0) {
      opts['project-code'] = projectCodes.sort().map(c => ({ value: c, label: c }));
    }

    // Client Code
    const clientCodes = [...new Set(clients.map(c => c.code).filter((c): c is string => !!c))];
    if (clientCodes.length > 0) {
      opts['client-code'] = clientCodes.sort().map(c => ({ value: c, label: c }));
    }

    // Client Name
    const clientNames = [...new Set(clients.map(c => c.name).filter(Boolean))];
    if (clientNames.length > 0) {
      opts['client-name'] = clientNames.sort().map(n => ({ value: n, label: n }));
    }

    // Project Manager (from employees)
    const managerNames = [...new Set(employees.map(e => e.name).filter(Boolean))];
    if (managerNames.length > 0) {
      opts['project-manager'] = managerNames.sort().map(n => ({ value: n, label: n }));
    }

    // Account Manager (same pool as employees)
    if (managerNames.length > 0) {
      opts['account-manager'] = managerNames.sort().map(n => ({ value: n, label: n }));
    }

    // Industry (from clients)
    const industries = [...new Set(clients.map(c => c.industry).filter(Boolean))];
    if (industries.length > 0) {
      opts['industry'] = industries.sort().map(i => ({ value: i, label: i }));
    }

    // Client Contact (from clients)
    const contacts = [...new Set(clients.map(c => c.contactName).filter(Boolean))];
    if (contacts.length > 0) {
      opts['client-contact'] = contacts.sort().map(c => ({ value: c, label: c }));
    }

    return opts;
  }, [allProjectsForSort, projects, clients, employees]);

  // Row click handler
  const handleRowClick = (project: Project) => {
    if (onNavigateToProject) {
      onNavigateToProject(project.id);
    } else {
      navigate(ROUTES.PROJECT_DETAILS(project.id));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="Projects"
        subtitle="Manage and monitor all your projects"
        size={20}
        showActions={true}
        actions={
          <Button 
            onClick={handleNewProjectClick}
            variant={hasMinimizedForm ? "info" : "default"}
          >
            <Plus className="h-4 w-4 mr-2" />
            {hasMinimizedForm ? 'Resume Project' : 'New Project'}
          </Button>
        }
      />

      {/* Data Sensitivity Toggle moved to header bar */}

      {/* Projects Table */}
      <Card ref={tableRef}>
        <CardContent className="space-y-4 pt-6">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by project code, name or client..."
                  value={searchTerm}
                  onChange={e => updateFilter('searchTerm', e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => updateFilter('searchTerm', '')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(v) => updateFilter('statusFilter', v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {selectableStatuses.length > 0
                  ? selectableStatuses.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))
                  : Object.entries(PROJECT_STATUS_LABELS).map(([id, label]) => (
                      <SelectItem key={id} value={id}>
                        {label}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
            <Select value={healthFilter} onValueChange={(v) => updateFilter('healthFilter', v as ProjectHealth | 'all')}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Health" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Health</SelectItem>
                <SelectItem value="green">Green</SelectItem>
                <SelectItem value="amber">Amber</SelectItem>
                <SelectItem value="red">Red</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={resetFilters}
                className="text-muted-foreground"
              >
                <X className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setFilterSheetOpen(true)}
              className="h-9 w-9 p-0 relative"
              aria-label="Filter"
            >
              <Filter className="h-4 w-4" />
              {activeColumnFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
                  {activeColumnFilterCount}
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setColumnConfigOpen(true)}
              className="h-9 w-9 p-0"
              aria-label="Configure"
            >
              <Settings2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Grid (replaces inline Table rendering) */}
          <ProjectsGrid
            data={paginatedProjects}
            referenceData={referenceData}
            columnDefinitions={columns}
            columnSorts={columnSorts}
            onColumnSortChange={setColumnSorts}
            onRowClick={handleRowClick}
            loading={loading}
            isTransitioning={isTransitioning}
            pageSize={pageSize}
            hasActiveFilters={hasActiveFilters}
            pagination={!loading && pagination.totalItems > 0 ? pagination : undefined}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </CardContent>
      </Card>

      {/* Project Creation Modal */}
      <ProjectCreationModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        mode="create"
        workflowStyle={preferences.projectCreationWorkflow}
        onProjectCreated={loadProjects}
      />

      {/* Column Configurator Drawer */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={handleSaveColumns}
        pageKey="projects-list"
      />

      {/* Project Filter Sheet */}
      <ProjectFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        visibleColumns={visibleColumns}
        filters={columnFilters}
        onApply={setColumnFilters}
        onClear={() => setColumnFilters({})}
        availableOptions={filterAvailableOptions}
      />
    </div>
  );
}
